import axios from 'axios'
import { useAuthStore } from '../store'

// v1.1.0 — deploy 2026-04-08
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

// Cache em memória para GET requests: { key -> { data, expiry } }
const _cache = new Map()

function cached(key, fn, ttlMs = 30000) {
  const hit = _cache.get(key)
  if (hit && Date.now() < hit.expiry) return Promise.resolve(hit.data)
  return fn().then(res => {
    _cache.set(key, { data: res, expiry: Date.now() + ttlMs })
    return res
  })
}

export function invalidateCache(prefix) {
  for (const key of _cache.keys()) {
    if (key.startsWith(prefix)) _cache.delete(key)
  }
}

// Timeout de 30s: cobre cold start do Koyeb e chamadas de IA (OpenAI pode demorar até 25s)
const api = axios.create({ baseURL: API_URL, timeout: 30000 })

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Navegação SPA sem reload — evita tela preta durante redirecionamento
// Componentes dentro do React escutam este evento para chamar navigate('/login')
export function onForceLogout(callback) {
  window.addEventListener('erp:force-logout', callback)
  return () => window.removeEventListener('erp:force-logout', callback)
}
function _forceLogout() {
  useAuthStore.getState().logout()
  _cache.clear()
  window.dispatchEvent(new Event('erp:force-logout'))
}

// Lock para evitar múltiplos refreshes simultâneos (race condition)
let _refreshPromise = null

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    // Só trata 401 com resposta real do servidor (não erros de rede)
    if (error.response?.status !== 401) return Promise.reject(error)

    const refreshToken = useAuthStore.getState().refreshToken
    if (!refreshToken) {
      _forceLogout()
      return Promise.reject(error)
    }

    // Se já há um refresh em andamento, aguarda ele terminar
    if (!_refreshPromise) {
      _refreshPromise = axios
        .post(`${API_URL}/auth/refresh`, { refresh_token: refreshToken }, { timeout: 10000 })
        .then(({ data }) => {
          useAuthStore.getState().setAuth(useAuthStore.getState().user, data.access_token, data.refresh_token)
          return data.access_token
        })
        .catch((refreshError) => {
          // Só força logout se o servidor rejeitou explicitamente o refresh token.
          // Erros de rede (Koyeb cold start, timeout) não devem deslogar o usuário.
          if (refreshError.response?.status === 401 || refreshError.response?.status === 403) {
            _forceLogout()
          }
          return null
        })
        .finally(() => { _refreshPromise = null })
    }

    const newToken = await _refreshPromise
    if (!newToken) return Promise.reject(error)

    error.config.headers.Authorization = `Bearer ${newToken}`
    return api(error.config)
  }
)

export default api

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  users: () => cached('auth_users', () => api.get('/auth/users'), 60000),
  invite: (data) => { invalidateCache('auth_users'); return api.post('/auth/invite', data) },
  updateUser: (id, data) => { invalidateCache('auth_users'); return api.put(`/auth/users/${id}`, data) },
  changePassword: (data) => api.put('/auth/change-password', data),
}

// Clients
export const clientsApi = {
  list: (params) => {
    const key = `clients_list_${JSON.stringify(params)}`
    return cached(key, () => api.get('/clients', { params }), 20000)
  },
  get: (id) => cached(`client_${id}`, () => api.get(`/clients/${id}`), 30000),
  create: (data) => { invalidateCache('client'); return api.post('/clients', data) },
  update: (id, data) => { invalidateCache('client'); return api.put(`/clients/${id}`, data) },
  delete: (id) => { invalidateCache('client'); return api.delete(`/clients/${id}`, { params: { hard: true } }) },
  import: (file) => {
    invalidateCache('client')
    const form = new FormData()
    form.append('file', file)
    return api.post('/clients/import/csv', form, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
}

// Sales
export const salesApi = {
  list: (params) => {
    const key = `sales_list_${JSON.stringify(params)}`
    return cached(key, () => api.get('/sales', { params }), 15000)
  },
  get: (id) => cached(`sale_${id}`, () => api.get(`/sales/${id}`), 30000),
  create: (data) => {
    invalidateCache('sale')
    invalidateCache('sales_dashboard')
    invalidateCache('product')   // estoque alterado
    invalidateCache('client')    // pontos de fidelidade alterados
    return api.post('/sales', data)
  },
  cancel: (id) => {
    invalidateCache('sale')
    invalidateCache('sales_dashboard')
    invalidateCache('product')   // estoque restaurado
    invalidateCache('client')    // pontos estornados
    return api.put(`/sales/${id}/cancel`)
  },
  dashboard: () => cached('sales_dashboard', () => api.get('/sales/dashboard'), 60000),
}

// Products
export const productsApi = {
  list: (params) => {
    const key = `products_list_${JSON.stringify(params)}`
    return cached(key, () => api.get('/products', { params }), 30000)
  },
  get: (id) => cached(`product_${id}`, () => api.get(`/products/${id}`), 30000),
  create: (data) => { invalidateCache('product'); return api.post('/products', data) },
  update: (id, data) => { invalidateCache('product'); return api.put(`/products/${id}`, data) },
  adjustStock: (data) => { invalidateCache('product'); return api.post('/products/stock/adjust', data) },
  lowStock: () => cached('products_low_stock', () => api.get('/products/low-stock'), 60000),
  categories: () => cached('products_categories', () => api.get('/products/categories'), 300000),
  uploadImages: (id, files) => {
    const form = new FormData()
    files.forEach(f => form.append('files', f))
    return api.post(`/products/${id}/images`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
}

// CRM
export const crmApi = {
  board: () => api.get('/crm/board'),
  leads: (params) => api.get('/crm/leads', { params }),
  leadByClient: (clientId) => api.get('/crm/leads', { params: { client_id: clientId } }),
  createLead: (data) => api.post('/crm/leads', data),
  moveLead: (id, data) => api.put(`/crm/leads/${id}/stage`, data),
  deleteLead: (id) => api.delete(`/crm/leads/${id}`),
  followupDashboard: (status) => api.get('/crm/followup/dashboard', { params: status ? { status } : {} }),
  followupStats: () => api.get('/crm/followup/stats'),
  followupConfig: () => api.get('/crm/followup/config'),
  rescheduleOverdue: () => api.post('/crm/followup/reschedule-overdue'),
  stats: () => api.get('/crm/stats'),
}

// Agents (IA)
export const agentsApi = {
  diagnose: (kpis) => api.post('/agents/diagnose', { kpis }),
  chat: (message, kpis, history) => api.post('/agents/chat', { message, kpis, history }),
  listVariations: (step_context) => api.get('/agents/variations', { params: { step_context } }),
  addVariation: (data) => api.post('/agents/variations', data),
  toggleVariation: (id) => api.put(`/agents/variations/${id}/toggle`),
  updateVariation: (id, body) => api.put(`/agents/variations/${id}`, { body }),
  deleteVariation: (id) => api.delete(`/agents/variations/${id}`),
  generateVariations: (data) => api.post('/agents/generate', data),
}

// WhatsApp
export const whatsappApi = {
  conversations: (config) => api.get('/whatsapp/conversations', config),
  messages: (number, limit, config) => api.get(`/whatsapp/messages/${number}`, { params: { limit }, ...config }),
  send: (data) => api.post('/whatsapp/send', data),
  sendMedia: (formData) => api.post('/whatsapp/send/media', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteMessage: (data) => api.post('/whatsapp/message/delete', data),
  reactMessage: (data) => api.post('/whatsapp/message/react', data),
  markRead: (chatid) => api.post('/whatsapp/message/markread', { chatid }),
  clearChat: (chatid) => api.post('/whatsapp/chat/clear', { chatid }),
  deleteConversation: (number) => api.delete(`/whatsapp/conversations/${number}`),
  clearHidden: () => api.delete('/whatsapp/conversations/hidden/clear-all'),
  status: () => api.get('/whatsapp/instance/status'),
  qrcode: () => api.get('/whatsapp/instance/qrcode'),
  createInstance: (name) => api.post('/whatsapp/instance/create', { instance_name: name }),
  disconnect: () => api.delete('/whatsapp/instance/disconnect'),
  debug: () => api.get('/whatsapp/instance/debug'),
  webhookStatus: () => api.get('/whatsapp/webhook/status'),
  webhookConfigure: (webhook_url) => api.post('/whatsapp/webhook/configure', { webhook_url }),
  webhookDebug: () => api.get('/webhooks/uazapi/debug'),
}

// NPS
export const npsApi = {
  dashboard: () => api.get('/nps/dashboard'),
  form: (token) => api.get(`/nps/form/${token}`),
  respond: (data) => api.post('/nps/respond', data),
}

// Financial
export const financialApi = {
  list: (params) => api.get('/financial', { params }),
  summary: (month) => api.get('/financial/summary', { params: { month_ref: month } }),
  pendingBills: (days) => api.get('/financial/pending-bills', { params: { days_ahead: days } }),
  create: (data) => api.post('/financial', data),
  update: (id, data) => api.put(`/financial/${id}`, data),
  syncFromSales: () => api.post('/financial/sync-from-sales'),
  closeMonth: (month) => api.post('/financial/close-month', null, { params: { month_ref: month } }),
}

// Exchanges
export const exchangesApi = {
  list: (params) => api.get('/exchanges', { params }),
  create: (data) => api.post('/exchanges', data),
  decide: (id, data) => api.put(`/exchanges/${id}/decide`, data),
}

// Tasks
export const tasksApi = {
  list: (params) => api.get('/tasks', { params }),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
}

// Goals & Ranking
export const goalsApi = {
  list: (params) => api.get('/goals', { params }),
  create: (data) => api.post('/goals', data),
  delete: (id) => api.delete(`/goals/${id}`),
  ranking: (month) => api.get('/goals/ranking', { params: { month_ref: month } }),
  sellerDashboard: (id) => api.get(`/goals/seller-dashboard/${id}`),
}

// Loyalty
export const loyaltyApi = {
  client: (id) => api.get(`/loyalty/client/${id}`),
  redeem: (data) => api.post('/loyalty/redeem', data),
  settings: () => api.get('/loyalty/settings'),
  updateSettings: (data) => api.put('/loyalty/settings', data),
  stats: () => api.get('/loyalty/stats'),
}

// Campaigns
export const campaignsApi = {
  list: () => api.get('/campaigns'),
  create: (data) => api.post('/campaigns', data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  sends: (params) => api.get('/campaigns/sends', { params }),
  generateVariations: (data) => api.post('/campaigns/generate-variations', data),
}

// Reports
export const reportsApi = {
  sales: (params) => api.get('/reports/sales', { params }),
  crmLosses: () => api.get('/reports/crm-losses'),
}

// Settings
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  alerts: (params) => api.get('/settings/alerts', { params }),
  markAlertRead: (id) => api.put(`/settings/alerts/${id}/read`),
}

// Ads
export const adsApi = {
  get: (params) => api.get('/ads', { params }),
  sync: () => api.post('/ads/sync'),
}
